// Proforma Detail Page
import React, { useEffect, useState } from 'react';
import { getProforma } from '../../../../services/finance_services/proformaService';
import { getProformaAttachments, downloadProformaAttachment } from '../../../../services/finance_attachment_services/proformaAttachmentService';
import { exportProformaPDF, exportProformaExcel } from '../../../../services/export_services/proformaExportService';
import { useParams, useNavigate } from 'react-router-dom';
import { formatCurrency, formatDate } from '../../../../utils/formatUtils';
import { Paperclip, Download, CheckCircle, AlertCircle, X } from 'lucide-react';
import useApi from '../../../../hooks/useApi';
import Message from '../../../../components/Message';

const ProformaDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [attachments, setAttachments] = useState([]);

  const [downloading, setDownloading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [notification, setNotification] = useState(null);
  
  // Use useApi for fetching proforma
  const { 
    data: proforma, 
    loading, 
    error, 
    callApi: fetchProforma 
  } = useApi(getProforma, {showSuccessToast: false});
  

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const result = await exportProformaExcel(id);
      
      if (!result.success) {
        throw new Error(result.error || 'Export failed');
      }
      
      setNotification({ type: 'success', message: 'Excel download successful!' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Excel download failed' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handleDownloadClick = async () => {
    setDownloading(true);
    try {
      const result = await exportProformaPDF(id);
      
      if (!result.success) {
        throw new Error(result.error || 'Export failed');
      }
      
      setNotification({ type: 'success', message: 'Download successful!' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Download failed' });
      setTimeout(() => setNotification(null), 3000);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await fetchProforma(id);
        const attachmentsData = await getProformaAttachments(id);
        setAttachments(attachmentsData?.data || []);
      } catch (err) {
        console.error('Failed to load attachments:', err);
      }
    };
    fetchData();
  }, [id]);

  const handleDownloadAttachment = async (attachmentId) => {
    try {
      setNotification({ type: 'loading', message: 'Downloading attachment...' });
      const result = await downloadProformaAttachment(id, attachmentId);
      
      if (!result.success) {
        throw new Error(result.error || 'Download failed');
      }
      
      setNotification({ type: 'success', message: 'Download successful!' });
      setTimeout(() => setNotification(null), 3000);
    } catch (err) {
      setNotification({ type: 'error', message: err.message || 'Download failed' });
      setTimeout(() => setNotification(null), 3000);
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (error) return <Message type="error" message={error} />;
  if (!proforma) return <div className="p-6">Proforma not found.</div>;

  // --- Financial calculations ---
  const items = proforma.items || [];
  const subtotal = items.reduce((sum, item) => sum + (item.unit_price * item.quantity - (item.discount_amount || 0)), 0);
  const totalDiscount = items.reduce((sum, item) => sum + (item.discount_amount || 0), 0);
  const totalTax = items.reduce((sum, item) => {
    const taxable = (item.unit_price * item.quantity - (item.discount_amount || 0));
    return sum + (taxable * (item.tax_rate || 0) / 100);
  }, 0);
  const grandTotal = subtotal + totalTax;
  const validityDays = proforma.expiry_date && proforma.date_issued ? Math.ceil((new Date(proforma.expiry_date) - new Date(proforma.date_issued)) / (1000*60*60*24)) : null;

  return (
    <div className="space-y-6">
      {/* Notification toast */}
      {notification && (
        <div className={`fixed top-5 right-5 z-50 flex items-center p-4 rounded-lg shadow-md ${
          notification.type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' :
          notification.type === 'error' ? 'bg-red-50 text-red-800 border border-red-200' :
          'bg-blue-50 text-blue-800 border border-blue-200'
        }`}>
          {notification.type === 'success' && <CheckCircle className="h-5 w-5 mr-2" />}
          {notification.type === 'error' && <AlertCircle className="h-5 w-5 mr-2" />}
          {notification.type === 'loading' && 
            <div className="h-5 w-5 mr-2 border-2 border-t-blue-600 border-r-blue-600 border-b-blue-100 border-l-blue-100 rounded-full animate-spin"></div>
          }
          <span>{notification.message}</span>
          <button 
            onClick={() => setNotification(null)}
            className="ml-3 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Proforma #{proforma.proforma_number || proforma.id}</h1>
          <div className="text-gray-600 mt-1">{proforma.title}</div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Download Buttons */}
          <button
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
            onClick={handleDownloadClick}
            disabled={downloading}
          >
            <Download className="h-4 w-4 mr-2" />
            {downloading ? 'Downloading...' : 'Download PDF'}
          </button>
          
          <button
            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
            onClick={handleDownloadExcel}
            disabled={downloadingExcel}
          >
            <Download className="h-4 w-4 mr-2" />
            {downloadingExcel ? 'Downloading...' : 'Download Excel'}
          </button>
          
          <button 
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700" 
            onClick={() => navigate(`/dashboard/proformas/edit/${proforma.id}`)}
          >
            Edit
          </button>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Client and Proforma Information */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-4 space-y-2 border">
            <div className="font-semibold text-gray-700 mb-3">Client Information</div>
            <div><span className="font-semibold">Name:</span> {proforma.client}</div>
            <div><span className="font-semibold">Email:</span> {proforma.client_email}</div>
            <div><span className="font-semibold">Phone:</span> {proforma.client_phone}</div>
            <div><span className="font-semibold">Address:</span> {proforma.client_address}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 space-y-2 border">
            <div className="font-semibold text-gray-700 mb-3">Proforma Details</div>
            <div><span className="font-semibold">Status:</span> {proforma.status_display}</div>
            <div><span className="font-semibold">Issued:</span> {formatDate(proforma.date_issued)}</div>
            <div><span className="font-semibold">Expiry:</span> {formatDate(proforma.expiry_date)}</div>
            {validityDays !== null && <div><span className="font-semibold">Validity:</span> {validityDays} days</div>}
            <div><span className="font-semibold">Items:</span> {items.length}</div>
          </div>
        </div>
        
        {/* Financial Breakdown */}
        <div className="bg-gray-50 rounded-lg p-4 border">
          <div className="font-semibold text-gray-700 mb-3">Financial Breakdown</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><span className="font-semibold">Subtotal:</span><br />{formatCurrency(subtotal)}</div>
            <div><span className="font-semibold">Total Discount:</span><br />{formatCurrency(totalDiscount)}</div>
            <div><span className="font-semibold">Total Tax:</span><br />{formatCurrency(totalTax)}</div>
            <div><span className="font-semibold">Grand Total:</span><br /><span className="font-bold text-lg">{formatCurrency(grandTotal)}</span></div>
          </div>
        </div>
        
        {/* Items Table */}
        <div>
          <div className="font-semibold text-gray-700 mb-3">Items</div>
          <div className="overflow-x-auto">
            <table className="w-full border rounded-lg text-sm">
              <thead>
                <tr className="bg-gray-100 text-left">
                  <th className="px-3 py-2">Description</th>
                  <th className="px-3 py-2">Qty</th>
                  <th className="px-3 py-2">Unit Price</th>
                  <th className="px-3 py-2">Discount</th>
                  <th className="px-3 py-2">Tax Rate</th>
                  <th className="px-3 py-2">Tax Amt</th>
                  <th className="px-3 py-2">Line Total</th>
                </tr>
              </thead>
              <tbody>
                {items.length > 0 ? (
                  items.map(item => {
                    const taxable = (item.unit_price * item.quantity - (item.discount_amount || 0));
                    const taxAmt = taxable * (item.tax_rate || 0) / 100;
                    return (
                      <tr key={item.id} className="border-t">
                        <td className="px-3 py-2">{item.description}</td>
                        <td className="px-3 py-2">{item.quantity}</td>
                        <td className="px-3 py-2">{formatCurrency(item.unit_price)}</td>
                        <td className="px-3 py-2">{formatCurrency(item.discount_amount)}</td>
                        <td className="px-3 py-2">{item.tax_rate}%</td>
                        <td className="px-3 py-2">{formatCurrency(taxAmt)}</td>
                        <td className="px-3 py-2">{formatCurrency(item.line_total)}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr><td colSpan={7} className="text-center py-4">No items.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Attachments */}
        <div>
          <h3 className="font-semibold text-gray-700 mb-3">Attachments</h3>
          {attachments.length === 0 ? (
            <div className="text-gray-500 bg-gray-50 rounded-lg p-4 text-center">No attachments found.</div>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4">
              <ul className="divide-y divide-gray-200">
                {attachments.map((attachment) => (
                  <li key={attachment.id} className="flex items-center justify-between py-3">
                    <div className="flex items-center gap-2">
                      <Paperclip className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{attachment.title || attachment.file_name || `Attachment #${attachment.id}`}</span>
                    </div>
                    <button
                      className="inline-flex items-center px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                      onClick={() => handleDownloadAttachment(attachment.id)}
                    >
                      <Download className="w-4 h-4 mr-1" /> Download
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        
        {/* Terms and Notes */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-4 border">
            <div className="font-semibold text-gray-700 mb-3">Terms & Conditions</div>
            <div className="text-gray-800 whitespace-pre-line text-sm">{proforma.terms_and_conditions || 'None'}</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 border">
            <div className="font-semibold text-gray-700 mb-3">Notes</div>
            <div className="text-gray-800 whitespace-pre-line text-sm">{proforma.notes || 'None'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProformaDetailPage;